import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG } from "../../../../shared/featureFlagNames";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { Toggle } from "../../Toggle";
import { CopyableValue } from "../CopyableValue";
import { ModalContent, ModalFooter } from "../shared";
import { DangerSection } from "../DangerSection";
import { ExistingSsoProvider } from "./ExistingSsoProvider";
import { NewSsoProvider } from "./NewSsoProvider";

const Container = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Card = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 24px;
  overflow: auto;
  box-shadow: var(--elevation-low);
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
`;

const Header = styled("div")`
  width: 100%;
  max-width: 648px;
  padding-left: 24px;
  margin-bottom: 32px;
`;

const ContentBox = styled("div")`
  width: 100%;
  max-width: 648px;
  padding: 24px;
  background: var(--surface-secondary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);

  & + & {
    margin-top: 24px;
  }
`;

const SessionSection = styled(ContentBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export function SSOSettings() {
  const z = useZero();
  const [providers] = useQuery(queries.ssoProviders());
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const isTenantAdmin = useHasAccess("allow_all");
  const showKillSwitch = useFeatureFlag(PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG);
  const [tenant] = useQuery(queries.currentTenant());
  const provider = providers[0];
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // Lives here so a token minted while registering a provider survives the
  // swap from NewSsoProvider to ExistingSsoProvider.
  const [scimToken, setScimToken] = useState<string | null>(null);

  if (!canManageSSO) {
    return (
      <Container>
        <Card>
          <Header>
            <Text variant="display-md">Single sign-on</Text>
            <div style={{ marginTop: 8 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Only admins can configure SSO
              </Text>
            </div>
          </Header>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Header>
          <Text variant="display-md">Single sign-on</Text>
          <div style={{ marginTop: 8 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Configure single sign-on for your tenant. Group mappings sync
              roles and space memberships on each sign-in.
            </Text>
          </div>
        </Header>

        <ContentBox>
          {provider ? (
            <ExistingSsoProvider
              provider={provider}
              onGeneratedScimToken={setScimToken}
            />
          ) : (
            <NewSsoProvider onGeneratedScimToken={setScimToken} />
          )}
        </ContentBox>

        <SessionSection>
          <div>
            <Text variant="text-regular-md">Sessions</Text>
            <div style={{ marginTop: 4 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Force all users to re-authenticate
              </Text>
            </div>
          </div>
          <Button
            variant="secondary"
            size="l"
            onClick={() => setConfirmRevoke(true)}
          >
            Revoke all sessions
          </Button>
        </SessionSection>

        {isTenantAdmin && showKillSwitch && tenant && (
          <SessionSection>
            <div>
              <Text variant="text-regular-md">Public routes</Text>
              <div style={{ marginTop: 4 }}>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Allow routes with public or external ID auth to serve requests
                  without signing in
                </Text>
              </div>
            </div>
            <Toggle
              isOn={!tenant.publicRouteAuthDisabled}
              onToggle={() =>
                z.mutate(
                  sharedMutators.setPublicRouteAuthDisabled({
                    disabled: !tenant.publicRouteAuthDisabled,
                    previousDisabled: tenant.publicRouteAuthDisabled ?? false,
                  })
                )
              }
            />
          </SessionSection>
        )}

        {provider && (
          <DangerSection
            style={{ width: "100%", maxWidth: 648, marginTop: 24 }}
            title="Delete SSO provider"
            description="Users will no longer be able to sign in with this SSO provider. This can’t be undone."
            actionLabel="Delete SSO provider"
            confirm={{
              confirmLabel: "Delete",
              onConfirm: () => {
                z.mutate(sharedMutators.deleteSSOProvider({ id: provider.id }));
                setScimToken(null);
              },
            }}
          />
        )}
        {scimToken && (
          <Modal open onClose={() => setScimToken(null)}>
            <ModalContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Text variant="text-regular-md">SCIM token created</Text>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Copy this token now — it won’t be shown again.
                </Text>
              </div>
              <CopyableValue value={scimToken}>
                <Text
                  variant="text-regular"
                  color="var(--text-secondary)"
                  style={{ flex: 1 }}
                >
                  {scimToken}
                </Text>
              </CopyableValue>
              <ModalFooter>
                <Button size="l" onClick={() => setScimToken(null)}>
                  Done
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
        <ConfirmModal
          open={confirmRevoke}
          onClose={() => setConfirmRevoke(false)}
          onConfirm={() => {
            z.mutate(sharedMutators.revokeAllSessions({}));
            setConfirmRevoke(false);
          }}
          title="Revoke all sessions"
          description="All other users will be signed out and must re-authenticate. Your current session will not be affected — sign out manually if you also need to re-authenticate."
          confirmLabel="Revoke all"
          destructive
        />
      </Card>
    </Container>
  );
}
